import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ClipboardCheck, Plus, Truck } from 'lucide-react';
import { getConcretagemRealizada } from '../services/concretagemRealizadaService';
import { getCaminhao } from '../services/caminhoesService';
import { formatDateBr, normalizeDateOnly } from '../utils/formatters';

const INITIAL_FILTROS = { empresa: '', obra: '', concretagem: '' };

// Concretagem da API -> formato usado na tela (previsto x cadastrado).
function normalizarConcretagem(item) {
  return {
    id: String(item.idConcretagemRealizada ?? item.id ?? ''),
    data: normalizeDateOnly(item.dataConcretagem || item.data_concretagem || ''),
    caminhoesPrevistos: Number(item.quantidadeCaminhao ?? item.quantidade_caminhao ?? 0) || 0,
    cpsPrevistos: Number(item.quantidadeTotalCp ?? item.quantidade_total_cp ?? 0) || 0,
    idObra: String(item.obra?.idObra ?? item.idObra ?? ''),
    obra: item.obra?.nomeObra || item.nomeObra || '-',
    idEmpresa: String(item.obra?.empresa?.idEmpresa ?? ''),
    empresa: item.obra?.empresa?.razaoSocial || '-',
  };
}

// Caminhão da API -> { previstos: qtde_cp_caminhao, cadastrados: corpos de prova já gravados }.
function normalizarCaminhao(item) {
  const cpsPrevistos = Number(item.qtdeCpCaminhao ?? item.qtde_cp_caminhao ?? 0) || 0;
  const cpsCadastrados = Array.isArray(item.corpoProvas) ? item.corpoProvas.length : 0;
  return {
    id: String(item.idCaminhao ?? item.id ?? ''),
    idConcretagem: String(
      item.concretagemRealizada?.idConcretagemRealizada ?? item.idConcretagemRealizada ?? ''
    ),
    placa: item.placaCaminhao || item.placa_caminhao || 'Sem placa',
    etapa: item.etapaObra?.nomeEtapaObra || '',
    cpsPrevistos,
    cpsCadastrados,
    cpsFaltando: Math.max(0, cpsPrevistos - cpsCadastrados),
  };
}

// Cor da pendência: vermelho sem nada cadastrado, âmbar parcial, verde fechado.
function corPendencia(faltam, cadastrados) {
  if (faltam <= 0) return 'text-emerald-700';
  return cadastrados > 0 ? 'text-amber-700' : 'text-red-700';
}

// Selo de pendência: vermelho quando nada foi cadastrado, âmbar quando falta parte,
// verde quando fechou.
function Pendencia({ faltam, cadastrados = 0, rotuloOk = 'completo' }) {
  if (faltam > 0) {
    const cores = cadastrados > 0
      ? 'bg-amber-100 text-amber-700'
      : 'bg-red-100 text-red-700';
    return (
      <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${cores}`}>
        faltam {faltam}
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
      {rotuloOk}
    </span>
  );
}

// Gestão do cadastro de concretagem: mostra, por concretagem, quantos caminhões ainda
// faltam cadastrar e, dentro de cada caminhão, quantos corpos de prova faltam.
function GestaoConcretagemPage() {
  const navigate = useNavigate();
  const [concretagens, setConcretagens] = useState([]);
  const [caminhoes, setCaminhoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [filtros, setFiltros] = useState(INITIAL_FILTROS);
  const [somentePendentes, setSomentePendentes] = useState(false);
  const [expandidos, setExpandidos] = useState([]);

  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      const [concretagensResult, caminhoesResult] = await Promise.allSettled([
        getConcretagemRealizada(),
        getCaminhao(),
      ]);

      if (!ativo) return;

      if (concretagensResult.status === 'rejected' || caminhoesResult.status === 'rejected') {
        setErro('Não foi possível carregar os dados de concretagem. Tente novamente.');
      }

      const listaConcretagens = Array.isArray(concretagensResult.value) ? concretagensResult.value : [];
      const listaCaminhoes = Array.isArray(caminhoesResult.value) ? caminhoesResult.value : [];

      setConcretagens(listaConcretagens.map(normalizarConcretagem).filter((item) => item.id));
      setCaminhoes(listaCaminhoes.map(normalizarCaminhao).filter((item) => item.idConcretagem));
      setLoading(false);
    };

    carregar().catch(() => {
      if (!ativo) return;
      setErro('Não foi possível carregar os dados de concretagem. Tente novamente.');
      setLoading(false);
    });

    return () => { ativo = false; };
  }, []);

  // Cruza concretagens e caminhões, já com os saldos do que falta cadastrar.
  const linhas = useMemo(() => {
    const porConcretagem = caminhoes.reduce((acc, caminhao) => {
      (acc[caminhao.idConcretagem] = acc[caminhao.idConcretagem] || []).push(caminhao);
      return acc;
    }, {});

    return concretagens
      .map((concretagem) => {
        const lista = porConcretagem[concretagem.id] || [];
        const cpsCadastrados = lista.reduce((total, item) => total + item.cpsCadastrados, 0);
        const caminhoesFaltando = Math.max(0, concretagem.caminhoesPrevistos - lista.length);
        const cpsFaltando = Math.max(0, concretagem.cpsPrevistos - cpsCadastrados);
        return {
          ...concretagem,
          caminhoes: lista,
          caminhoesCadastrados: lista.length,
          caminhoesFaltando,
          cpsCadastrados,
          cpsFaltando,
          pendente: caminhoesFaltando > 0 || cpsFaltando > 0 || lista.some((item) => item.cpsFaltando > 0),
        };
      })
      .sort((a, b) => String(b.data).localeCompare(String(a.data)));
  }, [concretagens, caminhoes]);

  const empresaOptions = useMemo(() => {
    const mapa = new Map();
    linhas.forEach((linha) => {
      if (linha.idEmpresa && !mapa.has(linha.idEmpresa)) mapa.set(linha.idEmpresa, linha.empresa);
    });
    return Array.from(mapa, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [linhas]);

  const obraOptions = useMemo(() => {
    const mapa = new Map();
    linhas
      .filter((linha) => !filtros.empresa || linha.idEmpresa === filtros.empresa)
      .forEach((linha) => {
        if (linha.idObra && !mapa.has(linha.idObra)) mapa.set(linha.idObra, linha.obra);
      });
    return Array.from(mapa, ([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [linhas, filtros.empresa]);

  const concretagemOptions = useMemo(() => (
    linhas.filter((linha) => (
      (!filtros.empresa || linha.idEmpresa === filtros.empresa)
      && (!filtros.obra || linha.idObra === filtros.obra)
    ))
  ), [linhas, filtros.empresa, filtros.obra]);

  const linhasFiltradas = useMemo(() => concretagemOptions.filter((linha) => (
    (!filtros.concretagem || linha.id === filtros.concretagem)
    && (!somentePendentes || linha.pendente)
  )), [concretagemOptions, filtros.concretagem, somentePendentes]);

  const totais = useMemo(() => linhasFiltradas.reduce((acc, linha) => ({
    concretagensPendentes: acc.concretagensPendentes + (linha.pendente ? 1 : 0),
    caminhoesFaltando: acc.caminhoesFaltando + linha.caminhoesFaltando,
    cpsFaltando: acc.cpsFaltando + linha.cpsFaltando,
  }), { concretagensPendentes: 0, caminhoesFaltando: 0, cpsFaltando: 0 }), [linhasFiltradas]);

  // Trocar empresa/obra invalida os filtros mais específicos que dependem dela.
  const handleFiltroEmpresa = (value) => setFiltros({ empresa: value, obra: '', concretagem: '' });
  const handleFiltroObra = (value) => setFiltros((prev) => ({ ...prev, obra: value, concretagem: '' }));
  const handleFiltroConcretagem = (value) => setFiltros((prev) => ({ ...prev, concretagem: value }));

  const toggleExpandido = (id) => {
    setExpandidos((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  // Atalhos para as telas de cadastro já com o registro escolhido.
  const irParaCadastroCaminhao = (idConcretagem) => navigate(`/app/caminhoes?concretagem=${idConcretagem}`);
  const irParaEdicaoCaminhao = (idCaminhao) => navigate(`/app/caminhoes?caminhao=${idCaminhao}`);

  const filtrosAtivos = Boolean(filtros.empresa || filtros.obra || filtros.concretagem || somentePendentes);

  const selectClass = 'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20';

  return (
    <div className="page-content-enter space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-slate-900">Gestão do cadastro de concretagem</h1>
        <p className="text-sm text-slate-500">
          Acompanhe o que ainda falta cadastrar: caminhões por concretagem e corpos de prova por caminhão.
        </p>
      </header>

      <section className="flex flex-wrap gap-8">
        {[
          { rotulo: 'Concretagens pendentes', valor: totais.concretagensPendentes },
          { rotulo: 'Caminhões a cadastrar', valor: totais.caminhoesFaltando },
          { rotulo: 'CPs a cadastrar', valor: totais.cpsFaltando },
        ].map((total) => (
          <div
            key={total.rotulo}
            className="min-w-[14rem] rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{total.rotulo}</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{total.valor}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1">
            <label htmlFor="filtro-empresa" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Empresa
            </label>
            <select
              id="filtro-empresa"
              value={filtros.empresa}
              onChange={(event) => handleFiltroEmpresa(event.target.value)}
              className={selectClass}
            >
              <option value="">Todas</option>
              {empresaOptions.map((empresa) => (
                <option key={empresa.id} value={empresa.id}>{empresa.nome}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="filtro-obra" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Obra
            </label>
            <select
              id="filtro-obra"
              value={filtros.obra}
              onChange={(event) => handleFiltroObra(event.target.value)}
              className={selectClass}
            >
              <option value="">Todas</option>
              {obraOptions.map((obra) => (
                <option key={obra.id} value={obra.id}>{obra.nome}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="filtro-concretagem" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Concretagem realizada
            </label>
            <select
              id="filtro-concretagem"
              value={filtros.concretagem}
              onChange={(event) => handleFiltroConcretagem(event.target.value)}
              className={selectClass}
            >
              <option value="">Todas</option>
              {concretagemOptions.map((linha) => (
                <option key={linha.id} value={linha.id}>
                  {formatDateBr(linha.data)} - {linha.obra}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={somentePendentes}
              onChange={(event) => setSomentePendentes(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20"
            />
            Somente com pendências
          </label>
          {filtrosAtivos ? (
            <button
              type="button"
              onClick={() => { setFiltros(INITIAL_FILTROS); setSomentePendentes(false); }}
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Limpar filtros
            </button>
          ) : null}
        </div>
      </section>

      {erro ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</p>
      ) : null}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="hidden overflow-x-auto sm:block">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
              <tr>
                <th className="w-10 px-4 py-3" />
                <th className="px-4 py-3">Empresa</th>
                <th className="px-4 py-3">Obra</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3">Caminhões</th>
                <th className="px-4 py-3">CPs</th>
                <th className="px-4 py-3">
                  <span className="flex justify-end normal-case tracking-normal">
                    Resultados: {linhasFiltradas.length}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={`sk-${idx}`} className="border-b border-slate-100">
                    {Array.from({ length: 7 }).map((__, cell) => (
                      <td key={cell} className="px-4 py-4">
                        <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : linhasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                    Nenhuma concretagem encontrada com os filtros atuais.
                  </td>
                </tr>
              ) : (
                linhasFiltradas.map((linha) => {
                  const aberto = expandidos.includes(linha.id);
                  return (
                    <React.Fragment key={linha.id}>
                      <tr
                        onClick={() => toggleExpandido(linha.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleExpandido(linha.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={aberto}
                        aria-label={`${aberto ? 'Recolher' : 'Ver'} caminhões da concretagem de ${linha.obra} em ${formatDateBr(linha.data)}`}
                        className="cursor-pointer border-b border-slate-100 align-middle hover:bg-slate-50/60 focus:bg-slate-50 focus:outline-none"
                      >
                        <td className="px-4 py-3">
                          <span
                            aria-hidden="true"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500"
                          >
                            {aberto ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700">{linha.empresa}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{linha.obra}</td>
                        <td className="px-4 py-3 text-slate-600">{formatDateBr(linha.data)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${corPendencia(linha.caminhoesFaltando, linha.caminhoesCadastrados)}`}>
                              {linha.caminhoesCadastrados}/{linha.caminhoesPrevistos || '-'}
                            </span>
                            <Pendencia faltam={linha.caminhoesFaltando} cadastrados={linha.caminhoesCadastrados} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${corPendencia(linha.cpsFaltando, linha.cpsCadastrados)}`}>
                              {linha.cpsCadastrados}/{linha.cpsPrevistos || '-'}
                            </span>
                            <Pendencia faltam={linha.cpsFaltando} cadastrados={linha.cpsCadastrados} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-start">
                            {linha.caminhoesFaltando > 0 ? (
                              <button
                                type="button"
                                onClick={(event) => { event.stopPropagation(); irParaCadastroCaminhao(linha.id); }}
                                className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-[#E7F1F4] px-3 py-1 text-xs font-medium text-[#0e7490] shadow-sm transition hover:bg-[#CFE3E9]"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Cadastrar caminhão
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                      {aberto ? (
                        <tr className="border-b border-slate-100 bg-slate-50/60">
                          <td colSpan={7} className="px-4 py-4">
                            {linha.caminhoes.length === 0 ? (
                              <p className="text-sm text-slate-500">
                                Nenhum caminhão cadastrado nesta concretagem.
                              </p>
                            ) : (
                              <table className="min-w-full text-left text-sm">
                                <thead className="text-[11px] uppercase tracking-[0.2em] text-slate-400">
                                  <tr>
                                    <th className="py-2 pr-4">Placa</th>
                                    <th className="py-2 pr-4">Etapa</th>
                                    <th className="py-2 pr-4">CPs</th>
                                    <th className="py-2 pr-4" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {linha.caminhoes.map((caminhao) => (
                                    <tr key={caminhao.id} className="border-t border-slate-200/70">
                                      <td className="py-2 pr-4 font-medium text-slate-800">
                                        <span className="inline-flex items-center gap-2">
                                          <Truck className="h-4 w-4 text-slate-400" />
                                          {caminhao.placa}
                                        </span>
                                      </td>
                                      <td className="py-2 pr-4 text-slate-600">{caminhao.etapa || '-'}</td>
                                      <td className="py-2 pr-4">
                                        <div className="flex items-center gap-2">
                                          <span className={`font-medium ${corPendencia(caminhao.cpsFaltando, caminhao.cpsCadastrados)}`}>
                                            {caminhao.cpsCadastrados}/{caminhao.cpsPrevistos || '-'}
                                          </span>
                                          <Pendencia faltam={caminhao.cpsFaltando} cadastrados={caminhao.cpsCadastrados} />
                                        </div>
                                      </td>
                                      <td className="py-2 pr-4">
                                        <div className="flex justify-start">
                                          {caminhao.cpsFaltando > 0 ? (
                                            <button
                                              type="button"
                                              onClick={() => irParaEdicaoCaminhao(caminhao.id)}
                                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[#0e7490]/30 px-3 py-1 text-xs font-medium text-[#0e7490] transition hover:bg-[#0e7490]/10"
                                            >
                                              <ClipboardCheck className="h-3.5 w-3.5" />
                                              Completar CPs
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {linha.caminhoesFaltando > 0 ? (
                              <p className="mt-3 text-xs text-amber-700">
                                Faltam {linha.caminhoesFaltando} caminhão(ões) para fechar os {linha.caminhoesPrevistos} previstos.
                              </p>
                            ) : null}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: cada concretagem vira um cartão com a mesma informação da tabela. */}
        <div className="divide-y divide-slate-100 sm:hidden">
          {loading ? (
            Array.from({ length: 3 }).map((_, idx) => (
              <div key={`skm-${idx}`} className="space-y-2 p-4">
                <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
              </div>
            ))
          ) : linhasFiltradas.length === 0 ? (
            <p className="p-6 text-center text-sm text-slate-500">
              Nenhuma concretagem encontrada com os filtros atuais.
            </p>
          ) : (
            linhasFiltradas.map((linha) => {
              const aberto = expandidos.includes(linha.id);
              return (
                <div key={linha.id} className="p-4">
                  <button
                    type="button"
                    onClick={() => toggleExpandido(linha.id)}
                    aria-expanded={aberto}
                    className="flex w-full items-start justify-between gap-3 text-left"
                  >
                    <div>
                      <p className="font-medium text-slate-900">{linha.obra}</p>
                      <p className="text-xs text-slate-500">{linha.empresa} - {formatDateBr(linha.data)}</p>
                    </div>
                    {aberto ? (
                      <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                    ) : (
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                    )}
                  </button>

                  <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-slate-500">Caminhões</span>
                    <span className={`font-medium ${corPendencia(linha.caminhoesFaltando, linha.caminhoesCadastrados)}`}>
                      {linha.caminhoesCadastrados}/{linha.caminhoesPrevistos || '-'}
                    </span>
                    <Pendencia faltam={linha.caminhoesFaltando} cadastrados={linha.caminhoesCadastrados} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-slate-500">CPs</span>
                    <span className={`font-medium ${corPendencia(linha.cpsFaltando, linha.cpsCadastrados)}`}>
                      {linha.cpsCadastrados}/{linha.cpsPrevistos || '-'}
                    </span>
                    <Pendencia faltam={linha.cpsFaltando} cadastrados={linha.cpsCadastrados} />
                  </div>

                  {aberto ? (
                    <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
                      {linha.caminhoes.length === 0 ? (
                        <p className="text-sm text-slate-500">Nenhum caminhão cadastrado nesta concretagem.</p>
                      ) : (
                        linha.caminhoes.map((caminhao) => (
                          <div key={caminhao.id} className="flex items-center justify-between gap-3">
                            <div className="text-sm">
                              <p className="font-medium text-slate-800">{caminhao.placa}</p>
                              <p className={`text-xs font-medium ${corPendencia(caminhao.cpsFaltando, caminhao.cpsCadastrados)}`}>
                                CPs {caminhao.cpsCadastrados}/{caminhao.cpsPrevistos || '-'}
                              </p>
                            </div>
                            {caminhao.cpsFaltando > 0 ? (
                              <button
                                type="button"
                                onClick={() => irParaEdicaoCaminhao(caminhao.id)}
                                className="rounded-full border border-[#0e7490]/30 px-3 py-1 text-xs font-medium text-[#0e7490]"
                              >
                                Completar CPs
                              </button>
                            ) : (
                              <Pendencia faltam={0} />
                            )}
                          </div>
                        ))
                      )}
                      {linha.caminhoesFaltando > 0 ? (
                        <button
                          type="button"
                          onClick={() => irParaCadastroCaminhao(linha.id)}
                          className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#E7F1F4] px-3 py-1 text-xs font-medium text-[#0e7490]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Cadastrar caminhão
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}

export default GestaoConcretagemPage;
